import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { LangLink } from "@/components/LangLink";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { ProductCard } from "@/components/ProductCard";
import { getCategory, CATEGORY_NAV } from "@/lib/api";
import { SITE_URL } from "@/lib/siteConfig";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

export default function CategoryPage() {
  const { slug } = useParams();
  const { lang, tx } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    getCategory(slug, lang)
      .then(setData)
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0 });
  }, [slug, lang]);

  if (notFound) {
    return (
      <div className="container-site py-24 text-center">
        <h1 className="font-display text-3xl">{tx("Category not found")}</h1>
        <Button asChild className="mt-6"><LangLink to="/">{tx("Back to home")}</LangLink></Button>
      </div>
    );
  }

  const related = CATEGORY_NAV.filter((c) => c.slug !== slug);
  const jsonLd = data ? {
    "@context": "https://schema.org", "@type": "CollectionPage",
    name: data.name, description: data.meta,
    url: `${SITE_URL}/collection/${slug}`,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: (data.products || []).map((p, i) => ({
        "@type": "ListItem", position: i + 1, name: p.title, url: p.url,
      })),
    },
  } : null;
  const faqLd = data?.faqs?.length ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: data.faqs.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
  } : null;

  return (
    <>
      {data && <Seo title={data.name} description={data.meta} path={`/collection/${slug}`} image={data.hero} jsonLd={jsonLd} />}
      {faqLd && <Seo schemaOnly jsonLd={faqLd} />}

      {/* HERO — navy chapter */}
      <section className="section-navy grain relative overflow-hidden">
        <div className="container-site grid items-end gap-8 py-14 lg:grid-cols-12 lg:gap-12 lg:py-20">
          <div className="lg:col-span-7">
            <Breadcrumb className="mb-6 [&_a]:text-white/60 [&_a:hover]:text-white [&_li]:text-white/60 [&_svg]:text-white/40">
              <BreadcrumbList>
                <BreadcrumbItem><BreadcrumbLink asChild><LangLink to="/">Home</LangLink></BreadcrumbLink></BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem><BreadcrumbPage className="text-white">{data?.name || slug}</BreadcrumbPage></BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            {loading ? <Skeleton className="h-12 w-72 bg-white/10" /> : (
              <>
                <p className="eyebrow text-[hsl(var(--primary))]">{tx("Kentucky Horsewear")}</p>
                <h1 className="mt-3 font-display text-4xl leading-[1.0] text-white sm:text-6xl">{data.h1}</h1>
                <div className="mt-5 h-px w-24 bg-[hsl(var(--primary))]" />
                <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-white/75">{data.intro}</p>
                {data.shop_all_url && (
                  <a href={data.shop_all_url} target="_blank" rel="noopener noreferrer" data-testid="category-shop-all"
                    className="mt-6 inline-flex items-center gap-2 text-sm font-medium uppercase tracking-[0.14em] text-white hover:text-[hsl(var(--primary))]">
                    {tx("Shop all on Boomerang.ie")} <ArrowUpRight className="h-4 w-4 text-[hsl(var(--primary))]" />
                  </a>
                )}
              </>
            )}
          </div>
          {data?.hero && (
            <figure className="lg:col-span-5">
              <div className="relative overflow-hidden rounded-2xl">
                <img src={data.hero} alt={data.name} className="aspect-[4/3] w-full object-cover" />
                <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/15" />
              </div>
            </figure>
          )}
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="container-site py-10">
        {loading ? (
          <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (<div key={i}><Skeleton className="aspect-[4/5] w-full rounded-lg" /><Skeleton className="mt-3 h-4 w-2/3" /></div>))}
          </div>
        ) : data?.products?.length ? (
          <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-4" data-testid="product-grid">
            {data.products.map((p, i) => <ProductCard key={p.handle} product={p} index={i} />)}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-border bg-secondary/50 p-12 text-center">
            <p className="font-display text-xl">{tx("Explore this range on Boomerang.ie")}</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">{tx("Browse the full collection with live stock and pricing.")}</p>
            {data?.shop_all_url && <Button asChild className="mt-5"><a href={data.shop_all_url} target="_blank" rel="noopener noreferrer">{tx("Shop")} {data?.name}</a></Button>}
          </div>
        )}
      </section>

      {/* SEO BODY */}
      {data?.body?.length > 0 && (
        <section className="container-site grid gap-12 py-8 lg:grid-cols-3">
          <div className="prose prose-neutral max-w-none lg:col-span-2 prose-headings:font-display">
            {data.body.map(([h, p], i) => (<div key={i}><h2>{h}</h2><p>{p}</p></div>))}
          </div>
          <aside className="h-fit space-y-6">
            {data?.faqs?.length > 0 && (
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-display text-lg">{tx("FAQs")}</h3>
                <Accordion type="single" collapsible className="mt-2">
                  {data.faqs.map(([q, a], i) => (
                    <AccordionItem key={i} value={`f-${i}`}>
                      <AccordionTrigger className="text-left text-sm font-medium">{q}</AccordionTrigger>
                      <AccordionContent className="text-sm text-muted-foreground">{a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
            <div className="rounded-xl border border-border bg-secondary/40 p-5">
              <h3 className="font-display text-lg">{tx("More categories")}</h3>
              <div className="mt-2 flex flex-col">
                {related.map((c) => (
                  <LangLink key={c.slug} to={`/collection/${c.slug}`} className="flex items-center justify-between rounded-md px-2 py-2 text-sm text-foreground hover:bg-background">
                    {tx(c.name)} <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </LangLink>
                ))}
              </div>
            </div>
          </aside>
        </section>
      )}
    </>
  );
}
